import { Box, Button, HStack, Text } from '@chakra-ui/react';
import React from 'react';
import ArrowIcon from '../Icons/ArrowIcon';
import { useIsMobile } from 'src/helpers/useIsMobile';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: string | number) => void;
}

const Paginator: React.FC<PaginationProps> = ({ currentPage, totalPages, onPageChange }) => {
  const isMobile = useIsMobile();
  const pagesToShow = isMobile ? 2 : 4;

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const pageNumbers = [...Array(totalPages)].map((_, index) => index + 1);
  const getPageNumbersToShow = () => {
    if (totalPages <= pagesToShow + 1) {
      return pageNumbers;
    }

    const firstPages = pageNumbers.slice(currentPage - 1, currentPage + pagesToShow - 1);
    const lastPage = pageNumbers[totalPages - 1];

    if (firstPages.includes(lastPage)) return firstPages;
    if (currentPage + pagesToShow === lastPage) return [...firstPages, lastPage];

    return [...firstPages, '...', lastPage];
  };

  const pageNumbersToShow = getPageNumbersToShow();

  return (
    <Box paddingBottom={isMobile ? '1em' : ''}>
      <HStack spacing={4} justify="center" align="center" marginTop={4}>
        <Button onClick={handlePreviousPage} disabled={currentPage === 1} variant="ghost" p={0}>
          <ArrowIcon direction="left" variantColor={'primary.main'} width={15} />
        </Button>

        <HStack spacing={2} wrap="wrap" justify="center">
          {pageNumbersToShow.map((page, index) =>
            page === '...' ? (
              <Text key={index}>...</Text>
            ) : (
              <Button
                key={page}
                onClick={() => onPageChange(page)}
                variant={currentPage === page ? 'solid' : 'outline'}
                colorScheme="teal"
              >
                {page}
              </Button>
            )
          )}
        </HStack>

        <Button onClick={handleNextPage} disabled={currentPage === totalPages} variant="ghost" p={0}>
          <ArrowIcon direction="right" variantColor={'primary.main'} width={15} />
        </Button>
      </HStack>
    </Box>
  );
};

export default Paginator;
